import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plane, Mail, Lock, ArrowRight, CheckCircle, Shield } from 'lucide-react'
import { requestMagicLink, verifyToken, loginWithPassword } from '../lib/api'
import { setAuthToken } from '../lib/auth'

export default function Login() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const next = searchParams.get('next') || '/admin'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [mode, setMode] = useState(token ? 'verifying' : 'choose')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [requires2FA, setRequires2FA] = useState(false)

  useEffect(() => {
    if (!token) return
    verifyToken(token).then(data => {
      if (data.success) { setAuthToken(data.token); window.location.href = next }
      else { setError('Invalid or expired link'); setMode('choose') }
    }).catch(() => { setError('Failed to verify link'); setMode('choose') })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMagicLink = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    try { await requestMagicLink(email, next); setSent(true) }
    catch { setError('Failed to send login link.') }
    finally { setLoading(false) }
  }

  const handlePasswordLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    try {
      const data = await loginWithPassword(email, password, totpCode)
      if (data.requires2FA) { setRequires2FA(true); setMode('2fa') }
      else if (data.success) { setAuthToken(data.token); window.location.href = next }
      else { setError(data.error || 'Invalid credentials') }
    } catch { setError('Login failed.') }
    finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-flight-cyan/20 flex items-center justify-center"><Plane className="w-6 h-6 text-flight-cyan" /></div>
            <span className="text-2xl font-bold text-white">FlightWall</span>
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2">Sign in to your account</h1>
        </div>
        <div className="glass-panel p-6">
          {mode === 'verifying' ? (
            <div className="text-center py-8"><div className="w-8 h-8 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-slate-400">Verifying...</p></div>
          ) : sent ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-slate-400 text-sm">We sent a magic link to <span className="text-white">{email}</span></p>
            </div>
          ) : mode === 'choose' ? (
            <div className="space-y-3">
              <button onClick={() => setMode('magic')} className="w-full p-4 rounded-xl border border-slate-700 bg-slate-800/50 hover:bg-slate-800 transition-all text-left flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-flight-cyan/20 flex items-center justify-center"><Mail className="w-5 h-5 text-flight-cyan" /></div>
                <div><div className="text-white font-medium">Magic Link</div><div className="text-slate-400 text-xs">No password needed</div></div>
                <ArrowRight className="w-4 h-4 text-slate-500 ml-auto" />
              </button>
              <button onClick={() => setMode('password')} className="w-full p-4 rounded-xl border border-slate-700 bg-slate-800/50 hover:bg-slate-800 transition-all text-left flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-flight-amber/20 flex items-center justify-center"><Lock className="w-5 h-5 text-flight-amber" /></div>
                <div><div className="text-white font-medium">Password</div><div className="text-slate-400 text-xs">Sign in with password and 2FA</div></div>
                <ArrowRight className="w-4 h-4 text-slate-500 ml-auto" />
              </button>
            </div>
          ) : mode === 'magic' ? (
            <form onSubmit={handleMagicLink}>
              <div className="mb-4">
                <label className="text-slate-400 text-sm mb-1 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" />
                </div>
              </div>
              {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-flight-cyan text-slate-950 font-bold hover:bg-flight-cyan/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" /> : <>Send Magic Link <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button type="button" onClick={() => setMode('choose')} className="w-full mt-3 py-2 text-slate-500 hover:text-white text-sm">← Back</button>
            </form>
          ) : mode === 'password' ? (
            <form onSubmit={handlePasswordLogin}>
              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required className="w-full pl-10 pr-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none" />
                  </div>
                </div>
              </div>
              {error && <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="w-full mt-6 py-3 rounded-lg bg-flight-cyan text-slate-950 font-bold hover:bg-flight-cyan/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button type="button" onClick={() => setMode('choose')} className="w-full mt-3 py-2 text-slate-500 hover:text-white text-sm">← Back</button>
            </form>
          ) : mode === '2fa' ? (
            <form onSubmit={handlePasswordLogin}>
              <div className="text-center mb-6">
                <Shield className="w-12 h-12 text-flight-cyan mx-auto mb-3" />
                <h2 className="text-lg font-bold text-white">Two-Factor Authentication</h2>
                <p className="text-slate-400 text-sm">Enter 6-digit code from authenticator app</p>
              </div>
              <div className="mb-4 flex justify-center">
                <input type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className="w-48 text-center text-2xl tracking-[0.5em] py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:border-flight-cyan focus:outline-none font-mono" />
              </div>
              {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">{error}</div>}
              <button type="submit" disabled={loading || totpCode.length !== 6} className="w-full py-3 rounded-lg bg-flight-cyan text-slate-950 font-bold hover:bg-flight-cyan/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" /> : <>Verify <ArrowRight className="w-4 h-4" /></>}
              </button>
              <button type="button" onClick={() => { setMode('password'); setTotpCode('') }} className="w-full mt-3 py-2 text-slate-500 hover:text-white text-sm">← Back</button>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  )
}
